import { GoogleMap } from '@react-google-maps/api';
import { useAuth, useUserSession } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyGetSupplierDevicesMapViewQuery } from 'store/services';
import { Device } from 'types';
import styles from './DeviceMap.module.scss';

import optimizedClusterMarker from 'assets/icons/circle-filled-48.png';
import dotError from 'assets/icons/dot-error.png';
import dotChartreuse from 'assets/icons/dot-chartreuse.png';
import dotYellow from 'assets/icons/dot-yellow.png';
import dotOrange from 'assets/icons/dot-orange.png';
import dotRed from 'assets/icons/dot-red.png';
import dotGreen from 'assets/icons/dot-green.png';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { isDeviceVisible } from './helpers';
import { DeviceMapSidePanel } from './components/DeviceMapSidePanel';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';

export type DeviceMapFilters = {
    v0: boolean;
    v20: boolean;
    v40: boolean;
    v60: boolean;
    v80: boolean;
    doCluster: boolean;
};

interface DeviceMapProps {
    queryConfig?: ElasticsearchDeviceBodyConfig | undefined;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export function DeviceMap({ queryConfig, viewAsSupplier }: DeviceMapProps) {
    const [loading, setLoading] = useState(false);
    const session = useUserSession();
    // center the map on the united states
    const [mapRef, setMapRef] = useState<google.maps.Map | undefined>();
    const [clustererRef, setClustererRef] = useState<MarkerClusterer>();
    const [devices, setDevices] = useState<Device[]>([]);
    const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
    const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(
        null
    );
    const defaultMapFilters = useMemo(
        () => ({
            v80: true,
            v60: true,
            v40: true,
            v20: true,
            v0: true,
            doCluster: true,
        }),
        []
    );

    const [mapFilters, setMapFilters] =
        useState<DeviceMapFilters>(defaultMapFilters);

    useEffect(() => {
        if (session?.map) {
            setMapFilters({
                ...defaultMapFilters,
                ...session.map,
            });
        }
    }, [defaultMapFilters, session?.map]);

    const center = useMemo(
        () => ({
            lat: 40.1451,
            lng: -99.668,
        }),
        []
    );

    const { user } = useAuth();
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [queryDevices] = useLazyGetSupplierDevicesMapViewQuery();

    useEffect(() => {
        if (viewAsSupplier && viewAsSupplier !== '') {
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/map`
            );
        } else {
            window.history.replaceState(null, '', '/map');
        }
    }, [viewAsSupplier]);

    const getTankIcon = (device: Device) => {
        const now = new Date().getTime();

        if (!device.estimated_tank || !device.lastReading?.time) {
            return dotError;
        }

        const deviceError = now - device.lastReading.time > 86400000 * 3;

        if (deviceError) {
            return dotError;
        }

        switch (true) {
            case device.estimated_tank >= 80:
                return dotGreen;
            case device.estimated_tank < 80 && device.estimated_tank >= 60:
                return dotChartreuse;
            case device.estimated_tank < 60 && device.estimated_tank >= 40:
                return dotYellow;
            case device.estimated_tank < 40 && device.estimated_tank >= 20:
                return dotOrange;
            case device.estimated_tank < 20:
                return dotRed;
            default:
                return dotGreen;
        }
    };

    const handleQueryDevices = useCallback(
        (queryConfig?: ElasticsearchDeviceBodyConfig) => {
            setLoading(true);

            const supplierId = viewAsSupplier || user?.protected?.supplierId;

            const hasSupplierId = supplierId?.toString() !== undefined;
            if (user && hasSupplierId) {
                const mapDevicesQueryConfig = {
                    userUid: user.uid ?? '',
                    supplierId: supplierId,
                    queryConfig: {
                        ...queryConfig,
                        limit: 10000,
                    },
                };
                queryDevices(mapDevicesQueryConfig).then((devices) => {
                    const { data } = devices;

                    const queriedDevices = data?.results;

                    if (queriedDevices) {
                        setDevices(queriedDevices);
                    }
                    setLoading(false);
                });
            }
        },
        [queryDevices, user, viewAsSupplier]
    );

    const onGoogleMapLoad = useCallback(
        (map: google.maps.Map) => {
            setMapRef(map);

            handleQueryDevices(queryConfig);
        },
        [handleQueryDevices, queryConfig]
    );

    useEffect(() => {
        handleQueryDevices(queryConfig);
    }, [handleQueryDevices, queryConfig]);

    useEffect(() => {
        markers.forEach((m) => m.setMap(null));

        const newMarkers = devices
            .filter((d) => isDeviceVisible(d, mapFilters))
            .map((device) => {
                const title = device.name || device.deviceId;
                const imgUrl = getTankIcon(device);

                const newMarker = new google.maps.Marker({
                    position: {
                        lat: device.lat,
                        lng: device.lng,
                    },
                    icon: {
                        url: imgUrl,
                        scaledSize: new google.maps.Size(20, 20),
                    },
                    title,
                });

                newMarker.addListener('click', () => {
                    setSelectedMarkerId(device.deviceId);
                    mapRef?.panTo({
                        lat: device.lat,
                        lng: device.lng,
                    });
                });
                return newMarker;
            });

        if (mapRef) {
            newMarkers.forEach((m) => m?.setMap(mapRef));
        }
        setMarkers(newMarkers as google.maps.Marker[]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices, mapFilters, mapRef]);

    useEffect(() => {
        if (clustererRef) {
            clustererRef.clearMarkers();
            if (mapFilters.doCluster) {
                markers.forEach((m) => m.setMap(null));
                clustererRef.addMarkers(markers as google.maps.Marker[]);
            }
        }
    }, [clustererRef, mapFilters.doCluster, markers]);

    useEffect(() => {
        if (mapRef) {
            const clusterer = new MarkerClusterer({
                map: mapRef,
                renderer: {
                    render(cluster) {
                        return new google.maps.Marker({
                            position: cluster.position,
                            label: {
                                text: String(cluster.markers?.length),
                                color: 'white',
                            },
                            icon: {
                                url: optimizedClusterMarker,
                                scaledSize: new google.maps.Size(48, 48),
                            },
                            optimized: true,
                        });
                    },
                },
            });

            setClustererRef(clusterer);
        }
    }, [mapRef]);

    if (!window.google)
        return (
            <div>
                google has not successfully loaded, contact an administrator if
                this problem persists.
            </div>
        );

    return (
        <Box
            className={`${styles.mapContainer} } ${
                isTabletOrMobile ? styles.tablet : ''
            }`}
        >
            {user?.protected && (
                <GoogleMap
                    zoom={4}
                    mapContainerClassName={styles.map}
                    center={center}
                    onLoad={onGoogleMapLoad}
                    onUnmount={() => setMapRef(undefined)}
                    options={{
                        clickableIcons: true,
                        disableDefaultUI: true,
                        disableDoubleClickZoom: true,
                        fullscreenControl: true,
                    }}
                />
            )}
            {loading || !mapRef ? (
                <div className={styles.loadingSpinnerContainer}>
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <DeviceMapSidePanel
                        setSelectedMarkerId={setSelectedMarkerId}
                        selectedMarkerId={selectedMarkerId}
                        devices={devices}
                        mapFilters={mapFilters}
                        mapRef={mapRef}
                        setMapFilters={setMapFilters}
                    />
                </>
            )}
        </Box>
    );
}
